import {
  INDUSTRIES,
  LIFECYCLE_LABEL,
  staffInsights,
  type ClientDetail,
  type Conflict,
  type DomainScore,
  type FollowUp,
  type InsightTarget,
  type RecordInsight,
} from '@cyberscore/contracts';
import {
  Activity,
  AlertTriangle,
  ClipboardCheck,
  Database,
  FileCheck2,
  FileText,
  FolderOpen,
  Gauge,
  Globe,
  IdCard,
  Landmark,
  LayoutGrid,
  ListChecks,
  Lock,
  MessagesSquare,
  RefreshCw,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import { can, useSession } from '../../lib/session';
import {
  ActivityList,
  FigureStrip,
  InsightRow,
  NextAction,
  RecordBody,
  RecordColumns,
  RecordFrame,
  RecordHero,
  useRecordTab,
  type ActivityItem,
  type Figure,
  type FolderTab,
  type Insight,
} from '../../ui/record/Record';
import { useAssessment } from '../assessments/api';
import { DocumentsPage } from '../documents/DocumentsPage';
import { InsurersPage } from '../insurers/InsurersPage';
import { ScorePage } from '../portfolio/ScorePage';
import { bandLabel, bandTone, pct, rm } from '../portfolio/format';
import type { ScoreView } from '../portfolio/api';
import { useAssessmentSignals } from '../record/signals';
import { useReviewAccess } from '../review/api';
import { useClient } from './api';
import { ClientGroup, ClientRecordCard, LifecyclePill, RenewalText, money } from './ClientRecord';
import { StatusPill, when } from './parts';

/**
 * Client 360 (task 100, decision 0073): one client as a whole, where an underwriter or broker lands. The
 * header says who it is and what is worth knowing first; the tabs hold the score, the client's
 * assessments, its insurers, documents, record and activity. A client with several assessments (one per
 * IT environment) shows one at a time, picked in the header.
 */

type TabId = '360' | 'score' | 'assessments' | 'insurers' | 'documents' | 'record' | 'activity';

const INSIGHT_ICON: Record<string, LucideIcon> = {
  cap: Lock,
  'below-minimum': AlertTriangle,
  'pending-kill-switches': ListChecks,
  conflict: AlertTriangle,
  'declared-not-verified': FileCheck2,
  'follow-up': MessagesSquare,
  'outside-in': Globe,
  provisional: Gauge,
  lift: ShieldCheck,
};

export function Client360({ customerKey }: { customerKey: string }) {
  const client = useClient(customerKey);
  if (client.isPending) return <div className="empty">Loading…</div>;
  if (client.isError)
    return (
      <div className="empty">
        <strong>Could not load this client</strong>
        {client.error.message}
      </div>
    );
  return <ClientPage detail={client.data} refetch={() => void client.refetch()} />;
}

function ClientPage({ detail, refetch }: { detail: ClientDetail; refetch: () => void }) {
  const { tenant } = useSession();
  const hash = useHash();
  const r = detail.record;
  const reviewer = useReviewAccess();
  const seesInsurers = can(tenant, 'assessments.view');
  const seesDocuments = can(tenant, 'evidence.view');

  // The assessment shown: ?a= when there are several, else the one updated last.
  const picked = hashQuery(hash).get('a');
  const shown = detail.assessments.find((a) => a.id === picked) ?? detail.assessments[0] ?? null;
  const aid = shown?.id ?? null;

  const tabs = useMemo<(FolderTab & { id: TabId })[]>(
    () => [
      { id: '360', label: 'Risk 360', icon: LayoutGrid },
      ...(aid ? [{ id: 'score' as const, label: 'Score', icon: Gauge }] : []),
      {
        id: 'assessments',
        label: 'Assessments',
        icon: ClipboardCheck,
        value: `${detail.assessments.length}`,
      },
      ...(aid && seesInsurers ? [{ id: 'insurers' as const, label: 'Insurers', icon: Landmark }] : []),
      ...(aid && seesDocuments ? [{ id: 'documents' as const, label: 'Documents', icon: FolderOpen }] : []),
      { id: 'record', label: 'Record', icon: IdCard },
      { id: 'activity', label: 'Activity', icon: Activity },
    ],
    [aid, detail.assessments.length, seesInsurers, seesDocuments],
  );
  const { active, select } = useRecordTab(tabs);

  const assessment = useAssessment(aid ?? '');
  const { signals, score, conflicts, followUps } = useAssessmentSignals(aid);

  const to = (t: InsightTarget): string => {
    if (!aid) return href.client(r.customer_key);
    switch (t.to) {
      case 'score':
      case 'outside_in':
      case 'mission':
        return href.client(r.customer_key, 'score');
      case 'documents':
        return href.client(r.customer_key, 'documents');
      case 'conflicts':
        return href.conflicts();
      case 'follow_ups':
      case 'answer':
        return reviewer.read ? href.review(aid) : href.assessment(aid);
    }
  };
  const insights: Insight[] = staffInsights(signals).map((x: RecordInsight) => ({
    id: x.id,
    icon: INSIGHT_ICON[x.id.split(':')[0]!] ?? Database,
    title: x.title,
    text: x.text,
    tone: x.tone,
    source: { label: x.source.label, href: to(x.source.target) },
    action: { label: x.action.label, href: to(x.action.target) },
  }));

  const activity = clientActivity(detail, score.data, conflicts, followUps);
  const pickAssessment = (id: string) => {
    const [path] = window.location.hash.split('?');
    const q = hashQuery(window.location.hash);
    q.set('a', id);
    navigate(`${path}?${q.toString()}`, { replace: true });
  };

  const facts = [
    r.customer_key,
    r.industry ? ((INDUSTRIES as Record<string, string>)[r.industry] ?? r.industry) : null,
    r.line_of_business,
    r.renewal_date ? `Renews ${new Date(`${r.renewal_date}T00:00:00`).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}` : null, // prettier-ignore
  ].filter((x): x is string => !!x);

  return (
    <RecordFrame>
      <RecordHero
        crumbs={[{ label: 'Clients', href: href.clients() }, { label: 'Client 360' }]}
        title={r.customer_name}
        pill={<LifecyclePill stage={r.lifecycle} />}
        subline={facts}
        actions={
          <>
            {detail.assessments.length > 1 && (
              <select
                className="input"
                aria-label="Assessment shown"
                value={aid ?? ''}
                onChange={(e) => pickAssessment(e.target.value)}
              >
                {detail.assessments.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.environment ?? a.title}
                  </option>
                ))}
              </select>
            )}
            {aid && (
              <a className="btn" href={href.assessment(aid)}>
                <ClipboardCheck size={16} /> Open assessment
              </a>
            )}
          </>
        }
        tabs={tabs}
        active={active}
        onSelect={(id) => select(id as TabId)}
        tabsLabel="Client contents"
      >
        <InsightRow
          title="Underwriting insights"
          note="From the answers, evidence, conflicts, follow-ups and the internet check. Each links to its source."
          insights={insights}
          empty={
            aid
              ? score.isPending
                ? 'Reading the assessment…'
                : 'Nothing stands out: no caps, contradictions or replies waiting.'
              : 'No assessment yet, so nothing to point out. Start one from Assessments with this client’s key.'
          }
        />
      </RecordHero>

      <RecordBody>
        {active === '360' && (
          <>
            <FigureStrip figures={figures(score.data)} />
            <RecordColumns
              main={
                <>
                  <Overview detail={detail} onEdit={() => select('record')} />
                  {score.data && <Domains domains={score.data.score.domains} />}
                </>
              }
              side={
                <>
                  <section className="card">
                    <div className="card-head">
                      <div className="card-title">Recent activity</div>
                      <button type="button" className="linklike small" onClick={() => select('activity')}>
                        View all
                      </button>
                    </div>
                    <ActivityList items={activity.slice(0, 4)} empty="Nothing has happened on this client yet." />
                  </section>
                  {assessment.data && (
                    <NextStep
                      status={assessment.data.status}
                      aid={assessment.data.id}
                      customerKey={r.customer_key}
                      conflicts={conflicts}
                      followUps={followUps}
                      reviewer={reviewer}
                      unanswered={score.data ? score.data.score.controls.applicable - score.data.score.controls.answered : null}
                    />
                  )}
                </>
              }
            />
          </>
        )}
        {active === 'score' && aid && <ScorePage id={aid} embedded />}
        {active === 'assessments' && <Assessments detail={detail} current={aid} />}
        {active === 'insurers' && aid && <InsurersPage id={aid} embedded />}
        {active === 'documents' && aid && <DocumentsPage id={aid} embedded />}
        {active === 'record' && <ClientRecordCard key={r.revision} detail={detail} refetch={refetch} />}
        {active === 'activity' && (
          <section className="card">
            <div className="card-head">
              <div className="card-title">Activity</div>
            </div>
            <ActivityList items={activity} empty="Nothing has happened on this client yet." />
          </section>
        )}
      </RecordBody>
    </RecordFrame>
  ); // prettier-ignore
}

function figures(v: ScoreView | undefined): Figure[] {
  if (!v)
    return [
      { icon: ShieldCheck, label: 'CyberScore', value: '–', extra: 'not scored yet' },
      { icon: FileCheck2, label: 'Evidence grade', value: '–' },
      { icon: Database, label: 'Expected annual loss', value: '–' },
      { icon: Lock, label: 'Score cap', value: '–' },
    ];
  const r = v.score;
  return [
    {
      icon: ShieldCheck,
      label: 'CyberScore',
      value: (
        <>
          {r.score}
          <span className={`pill ${bandTone(r.band)}`}>{bandLabel(r.band)}</span>
        </>
      ),
      extra: r.provisional ? '/ 1000 · provisional' : '/ 1000',
    },
    { icon: FileCheck2, label: 'Evidence grade', value: v.grade.grade, extra: `${pct(r.controls.evidence_confidence)} confidence` }, // prettier-ignore
    {
      icon: Database,
      label: 'Expected annual loss',
      value: r.loss ? rm(r.loss.expected_annual) : '–',
      extra: r.loss ? undefined : 'needs revenue',
    },
    {
      icon: Lock,
      label: 'Score cap',
      value: r.kill_switches.cap ?? 'None',
      extra: r.kill_switches.pending.length ? `${r.kill_switches.pending.length} open` : undefined,
    },
  ];
}

function Overview({ detail, onEdit }: { detail: ClientDetail; onEdit: () => void }) {
  const r = detail.record;
  const left: [string, React.ReactNode][] = [
    ['Insured', r.customer_name],
    ['Industry', r.industry ? ((INDUSTRIES as Record<string, string>)[r.industry] ?? r.industry) : '–'],
    ['Country', r.country ?? '–'],
    ['Stage', LIFECYCLE_LABEL[r.lifecycle]],
    ['Group', detail.group.parent ? `Subsidiary of ${detail.group.parent.name}` : detail.group.subsidiaries.length ? `Parent of ${detail.group.subsidiaries.length}` : '–'], // prettier-ignore
  ];
  const right: [string, React.ReactNode][] = [
    ['Policy', r.policy_number ?? '–'],
    ['Line', r.line_of_business ?? '–'],
    ['Broker', r.broker ?? '–'],
    ['Sum insured', money(r.sum_insured)],
    ['Renewal', <RenewalText key="r" date={r.renewal_date} />],
  ];
  return (
    <section className="card">
      <div className="card-head">
        <div className="card-title">Underwriting overview</div>
        {detail.can_edit && (
          <button type="button" className="linklike small" onClick={onEdit}>
            Edit
          </button>
        )}
      </div>
      <div className="c360-kv">
        {[left, right].map((rows, i) => (
          <dl key={i}>
            {rows.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
    </section>
  );
}

/** How far each domain's answers are backed: what was declared, and what the evidence carries of it. */
function domainState(d: DomainScore): [string, string] {
  if (!d.answered) return ['Not answered', 'neutral'];
  if (d.score < 50) return ['Gap', 'bad'];
  const backed = d.score ? d.effective / d.score : 0;
  if (backed >= 0.8) return ['Backed', 'ok'];
  if (backed >= 0.6) return ['Partly backed', 'neutral'];
  return ['Declared only', 'warn'];
}

function Domains({ domains }: { domains: DomainScore[] }) {
  const rows = [...domains].sort((a, b) => b.weight - a.weight);
  return (
    <section className="card">
      <div className="card-head">
        <div className="card-title">Control domains</div>
        <span className="c360-legend">
          <span>
            <i className="declared" /> Declared
          </span>
          <span>
            <i className="backed" /> Backed by evidence
          </span>
        </span>
      </div>
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Domain</th>
              <th className="right">Weight</th>
              <th>Declared and backed</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const [label, tone] = domainState(d);
              return (
                <tr key={d.domain}>
                  <td>{d.name}</td>
                  <td className="right num">{pct(d.weight)}</td>
                  <td>
                    <span
                      className="c360-bar"
                      title={`Declared ${d.score}, backed ${Math.round(d.effective)}`}
                    >
                      <i className="declared" style={{ width: `${d.score}%` }} />
                      <i className="backed" style={{ width: `${d.effective}%` }} />
                    </span>
                  </td>
                  <td>
                    <span className={`pill ${tone}`}>{label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Assessments({ detail, current }: { detail: ClientDetail; current: string | null }) {
  return (
    <>
      <section className="card">
        <div className="card-head">
          <div className="card-title">Assessments</div>
          <a className="linklike small" href={href.assessments(`?customer=${encodeURIComponent(detail.record.customer_key)}`)}>
            Open in Assessments
          </a>
        </div>
        {detail.assessments.length ? (
          <ul className="plain-list c360-list">
            {detail.assessments.map((a) => (
              <li key={a.id} className={a.id === current ? 'current' : undefined}>
                <div>
                  <a href={href.assessment(a.id)}>{a.title}</a>
                  {a.environment && <span className="muted"> · {a.environment}</span>}
                  <div className="muted small">Updated {when(a.updated_at)}</div>
                </div>
                <span className="row gap-s">
                  {a.score !== null && a.band && (
                    <>
                      <b className="num">{a.score}</b>
                      <span className={`pill ${bandTone(a.band as never)}`}>{bandLabel(a.band as never)}</span>
                    </>
                  )}
                  <StatusPill status={a.status as never} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small rec-pad">
            No assessment yet. Start one from Assessments with the key {detail.record.customer_key}.
          </p>
        )}
      </section>
      <section className="card pad">
        <ClientGroup detail={detail} />
      </section>
    </>
  ); // prettier-ignore
}

function NextStep({
  status,
  aid,
  customerKey,
  conflicts,
  followUps,
  reviewer,
  unanswered,
}: {
  status: string;
  aid: string;
  customerKey: string;
  conflicts: Conflict[];
  followUps: FollowUp[];
  reviewer: { read: boolean; write: boolean };
  unanswered: number | null;
}) {
  const open = conflicts.filter((c) => c.state === 'open' || c.state === 'disputed');
  const replies = followUps.filter((f) => f.state === 'answered');
  if (reviewer.write && (status === 'submitted' || status === 'in_review'))
    return <NextAction title="Review the answers" detail="Submitted and waiting on underwriting." action={{ label: 'Review', href: href.review(aid) }} />; // prettier-ignore
  if (reviewer.write && replies.length)
    return <NextAction title={`Read ${replies.length === 1 ? 'the business’s reply' : `${replies.length} replies`}`} detail="To follow-up questions on their answers." action={{ label: 'Open', href: href.review(aid) }} />; // prettier-ignore
  if (open.length && reviewer.write)
    return <NextAction title={`Decide ${open.length === 1 ? 'a conflict' : `${open.length} conflicts`}`} detail="Answers the evidence contradicts." action={{ label: 'Open conflicts', href: href.conflicts() }} />; // prettier-ignore
  if (status === 'in_remediation')
    return <NextAction title="Waiting on the business" detail="To change the answers asked about." action={{ label: 'Message them', href: href.messages(aid) }} />; // prettier-ignore
  if (status === 'in_progress')
    return <NextAction title="Waiting on the business to finish" detail={unanswered ? `${unanswered} controls without an answer yet.` : 'Nearly done.'} action={{ label: 'Message them', href: href.messages(aid) }} />; // prettier-ignore
  if (status === 'decided')
    return <NextAction title="Place with insurers" detail="Fill each insurer’s form and compare their quotes." action={{ label: 'Insurers', href: href.client(customerKey, 'insurers') }} />; // prettier-ignore
  return null;
}

/** What has happened on the client, newest first, from what its record and assessment already say. */
function clientActivity(
  detail: ClientDetail,
  score: ScoreView | undefined,
  conflicts: Conflict[],
  followUps: FollowUp[],
): ActivityItem[] {
  const r = detail.record;
  const items: (ActivityItem & { at: string })[] = [];
  for (const a of detail.assessments)
    items.push({ id: `a:${a.id}`, icon: ClipboardCheck, at: a.updated_at, when: when(a.updated_at), title: `${a.title} updated`, text: a.environment ?? undefined, href: href.assessment(a.id) }); // prettier-ignore
  for (const h of score?.history.slice(0, 5) ?? [])
    items.push({ id: `s:${h.seq}`, icon: RefreshCw, at: h.created_at, when: when(h.created_at), title: `Scored ${h.score} (${bandLabel(h.band)})`, text: h.kill_switch_cap !== null ? `Capped at ${h.kill_switch_cap}` : h.provisional ? 'Provisional' : undefined }); // prettier-ignore
  for (const c of conflicts)
    items.push({ id: `c:${c.id}`, icon: AlertTriangle, at: c.found_at, when: when(c.found_at), title: 'Answer contradicted', text: `${c.question_text} · ${c.source_name}` }); // prettier-ignore
  for (const f of followUps)
    if (f.reply) items.push({ id: `f:${f.id}`, icon: MessagesSquare, at: f.reply.at, when: when(f.reply.at), title: 'The business replied to a follow-up', text: f.question_text }); // prettier-ignore
  if (r.updated_at)
    items.push({ id: 'record', icon: FileText, at: r.updated_at, when: when(r.updated_at), title: 'Record saved', text: r.updated_by_name ? `By ${r.updated_by_name}` : undefined }); // prettier-ignore
  return items.sort((a, b) => b.at.localeCompare(a.at));
}
